import { Helmet } from 'react-helmet-async';
import PageLayout from '../../components/PageLayout';
import { MapPin, CheckCircle2, Building2 } from 'lucide-react';

const ContemporaryTaliparamba = () => {
  return (
    <>
      <Helmet>
        <title>Contemporary Home Building in Taliparamba | Case Study</title>
        <meta name="description" content="Discover how Master Home completed this contemporary residential project in Taliparamba. Expert construction overcoming coastal climate challenges." />
        <link rel="canonical" href="https://masterhome.co.in/projects/contemporary-house-taliparamba" />
      </Helmet>

      <PageLayout
        title="Contemporary House in Taliparamba"
        subtitle="A perfect blend of Kerala tradition and cutting-edge contemporary design."
      >
        <div className="space-y-16">
          {/* Hero Gallery */}
          <div className="rounded-2xl overflow-hidden shadow-2xl relative">
            <img
              src="/villa-builders-taliparamba.jpg"
              alt="Contemporary home construction by villa builders in Taliparamba"
              className="w-full h-auto aspect-[21/9] object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-8">
              <h2 className="text-3xl font-bold text-white">Project Value: ₹95 Lakhs</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="md:col-span-2 space-y-12">
              {/* Overview */}
              <section className="prose max-w-none text-gray-700">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b pb-4">Project Overview</h2>
                <p className="text-lg">
                  Situated in the developing suburbs of Taliparamba, this contemporary house was designed for a retired couple desiring an expansive ground floor living arrangement. The project demanded heavy emphasis on accessibility without sacrificing premium aesthetics.
                </p>
                <div className="flex flex-wrap gap-4 mt-6">
                  <span className="bg-primary-50 text-primary-700 px-4 py-2 rounded-full font-semibold flex items-center gap-2"><MapPin size={18} /> Dharmasala, Taliparamba</span>
                  <span className="bg-primary-50 text-primary-700 px-4 py-2 rounded-full font-semibold flex items-center gap-2"><Building2 size={18} /> Retirement Home</span>
                </div>
              </section>

              {/* Challenge */}
              <section className="bg-yellow-50 p-8 rounded-2xl border border-yellow-100">
                <h3 className="text-2xl font-bold text-yellow-900 mb-4">The Challenge</h3>
                <ul className="space-y-4 text-yellow-800">
                  <li className="flex gap-3"><span className="font-bold shrink-0">1. Spatial Efficiency:</span> The client required 3 bedrooms entirely on the ground floor to minimize stair usage, demanding highly efficient use of the 12-cent plot.</li>
                  <li className="flex gap-3"><span className="font-bold shrink-0">2. Coastal Humidity:</span> Taliparamba's proximity to coastal winds necessitated specialized anti-corrosive treatments for all external ironwork and grills.</li>
                  <li className="flex gap-3"><span className="font-bold shrink-0">3. Material Scarcity:</span> During the peak construction window, specific high-grade teak wood requested by the client was in severe shortage across the district.</li>
                </ul>
              </section>

              {/* Solution */}
              <section className="bg-teal-50 p-8 rounded-2xl border border-teal-100">
                <h3 className="text-2xl font-bold text-teal-900 mb-4">The Solution & Execution</h3>
                <p className="text-teal-800 mb-6">
                  Our architecture drafting team spent 3 iterations perfecting a wide-body floor plan that naturally flowed between living areas without long corridors. To handle the coastal rusting, we avoided standard mild steel, opting instead for fully galvanized iron (GI) sections treated with epoxy primers.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-teal-200">
                    <h4 className="font-bold flex items-center gap-2 mb-2"><CheckCircle2 className="text-teal-600" /> Sourcing Network</h4>
                    <p className="text-sm text-gray-600">Leveraged our wide supplier network to source seasoned Nilambur teak directly from wholesale mills.</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-teal-200">
                    <h4 className="font-bold flex items-center gap-2 mb-2"><CheckCircle2 className="text-teal-600" /> Custom Joinery</h4>
                    <p className="text-sm text-gray-600">Built extensive custom wardrobes and kitchen cabinets on-site using premium moisture-resistant plywood.</p>
                  </div>
                </div>
              </section>

            </div>

            {/* Sidebar Details */}
            <div className="space-y-8">
              <div className="glass-card p-6 bg-gray-50 rounded-2xl border border-gray-100">
                <h3 className="text-xl font-bold mb-6">Project Details</h3>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Square Footage</span>
                    <span className="font-bold text-gray-900">2,450 sq.ft</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Duration</span>
                    <span className="font-bold text-gray-900">9 Months</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Bedrooms</span>
                    <span className="font-bold text-gray-900">4 BHK (3 Ground)</span>
                  </div>
                  <div className="flex justify-between pb-2">
                    <span className="text-gray-500">Cost Range</span>
                    <span className="font-bold text-gray-900">₹90L - 1 Cr</span>
                  </div>
                </div>
              </div>

              {/* Client Testimonial */}
              <div className="bg-primary-900 text-white p-8 rounded-2xl shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 text-9xl text-white/10 font-serif leading-none transform translate-x-4 -translate-y-4">"</div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-yellow-400 font-bold text-xl">★★★★★</span>
                </div>
                <p className="relative z-10 text-lg leading-relaxed mb-6 font-medium italic">
                  "Finding reliable home builders in Taliparamba was a challenge until we met the Master Home team. They understood our need for a single-story feel and delivered a beautifully finished contemporary house."
                </p>
                <div className="flex items-center gap-4 border-t border-white/20 pt-4">
                  <div className="bg-secondary-500 w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl text-primary-900">K</div>
                  <div>
                    <h4 className="font-bold">Krishnan & Latha</h4>
                    <p className="text-primary-200 text-sm">Retirees, Taliparamba</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12 bg-gray-50 p-8 rounded-2xl border border-gray-200">
            <h2 className="text-2xl font-bold mb-4">Start Your Construction Journey</h2>
            <a href="tel:+919567810944" className="inline-flex items-center justify-center px-8 py-4 text-white bg-primary-600 rounded-full font-bold hover:bg-primary-700 transition-colors shadow-lg">
              Speak With Our Architects
            </a>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default ContemporaryTaliparamba;
